import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { getMinMaxReservationTimeAsyc, postQuotationAsync, postReservationDryRunAsync } from "src/api/reservation/reservation-api";
import { AvailableReserveTimes, QuotationRequest, ScheduleData } from "src/api/reservation/reservation-type";
import BaseButton from "src/components/BaseButton";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import Link from "src/components/PartnerLink";
import TaapSpaceBuilding from "src/pages/building/components/TaapSpaceBuilding";
import SwiperViewer from "src/pages/components/SwiperViewer";
import { getSalePeriodMessage, minutesToDisplayText, YmdFormat } from "src/utils/common-util";
import { getBaseUrl } from "src/utils/app-Info";
import SwiperCore, { Navigation, Pagination, Parallax } from "swiper";
import { useRecaptcha } from "src/hooks/useRecaptcha";
import { getClosedDays } from "../hooks/closing-policy";
import useProductFacility from "../hooks/useProductFacility";
import useSchedule from "../hooks/useSchedule";
import FacilityReservation from "./components/FacilityReservation";
import FacilityUsageInfo from "./components/FacilityUsageInfo";
import ReservationTimeAndPrice from "./components/ReservationTimeAndPrice";

import IssueModalRenderer from "src/pages/contract/apply/components/IssueModalRenderer";
import { Modal } from "src/pages/visitor/visitor-types";

/**
 * 공용공간 상세
 */

SwiperCore.use([Parallax, Navigation, Pagination]);

// 분을 HH:MM 형태로 변환 (예: 90 -> "01:30")
const minutesToHHMM = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};

// 시간 slot 선택시 예약 가능 유효성 검사 초기세팅
const validateDefault = {
  minTime: { message: "", isDisabled: true },
  maxTime: { message: "", isDisabled: true },
  availableTimes: { message: "", isDisabled: true },
};

const FacilityDetail = () => {
  const navigate = usePartnerNavigate();
  const location = useLocation();

  const { productId, facilityId } = useParams();

  const [confirmModal, setConfirmModal] = useState<Modal>({
    isOpen: false,
  });
  //예약구간 > 최대시간에 따른 slot 비활성화를 위해 따로 state 만듬

  // 캘린더에 그려진 날짜 범위 > 휴무일(하루종일) 표시 계산에 사용
  const [calendarRange, setCalendarRange] = useState<{ startDate: string; endDate: string }>();

  // 스케줄표에서 선택한 시간들
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleData[]>([]);
  // 슬롯 선택시 유효성 텍스트 데이터
  const [reserveTimeValidation, setReserveTimeValidation] = useState(validateDefault);

  // 예약 최소,최대시간 데이터
  const [userMinMaxTime, setUserMinMaxTime] = useState<AvailableReserveTimes>();

  // 데이터 로딩 완료 여부
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // 새로고침 진행 여부 > 클릭 1회당 7개 요청이 나가므로 진행 중에는 추가 클릭을 막는다
  const isRefreshingRef = useRef(false);

  // 상품/공용공간 상세 hook
  const { facility, product, building, mediaSorting, refetchProduct } = useProductFacility({
    productId: String(productId),
    facilityId: String(facilityId),
  });

  //공용공간 시간 slots 영역 커스텀 훅 > [요금, 예약 정책, 점유, 예약가능 구간]
  const { scheduleSlots, calendardate, setCalendarDate, updatedAt, reserveDuration, policies, closingRecurrences, refetchSchedule } = useSchedule({
    product,
    facility,
  });

  // 예약 시작시간 정책 > 유효 예약 정책 중 예약 시작 시간 설정
  const reservationPolicy = useMemo(
    () => policies.find((policy) => policy.key.policyType === "start-time-policy")?.details.startTimePolicy,
    [policies],
  );

  // 휴무일 목록 (하루종일) > 휴무일 정책을 캘린더에 그려진 날짜 범위로 전개
  const closedDays = useMemo(
    () => (calendarRange ? getClosedDays(calendarRange.startDate, calendarRange.endDate, closingRecurrences) : []),
    [calendarRange, closingRecurrences],
  );

  // scheduleSlots 에서 가장 낮은 금액
  const minimumPrice = useMemo(() => {
    return scheduleSlots.reduce((min, current) => (current.price && min.price && current.price < min.price ? current : min), scheduleSlots[0]);
  }, [scheduleSlots]);

  // 시작일 && 종료일 조건 및 메세지 노출
  const availableSalePeriod = useMemo(() => {
    if (product) {
      return getSalePeriodMessage(product);
    }
  }, [product]);

  // 견적서 등록 api
  const { executeAsync: postQuotation } = useApiOperation(postQuotationAsync, { noAuthenticationRequired: true });

  // 견적서 등록(무인증)은 policy-based challenge reCAPTCHA 사용
  const { getToken, siteKey } = useRecaptcha({ mode: "policy-based" });

  //예약 가능시간 조회 > 최소 ~ 최대 예약해야함
  const { executeAsync: getMinMaxReservationTime } = useApiOperation(getMinMaxReservationTimeAsyc, { noAuthenticationRequired: true });

  // 상품 공용공간 예약 검증
  const { executeAsync: postReservationDryRun } = useApiOperation(postReservationDryRunAsync, { noAuthenticationRequired: true });

  // 예약의 최소/최대 예약시간 조회
  const fetchMinMaxReserveTime = useCallback(async () => {
    const { data } = await getMinMaxReservationTime({
      serviceId: String(productId),
      serviceType: "SERVICE_PRODUCT",
      commonFacilityType: facility?.commonFacilityType || "COMMONFACILITY_UNRECOGNIZED",
      buildingCommonFacilityId: String(facilityId),
    });

    // executeAsync 는 에러를 throw 하지 않고 에러 객체를 반환하므로 data 가 undefined 일 수 있다
    if (data?.data) {
      setUserMinMaxTime(data.data.content[0]);
    }
  }, [productId, facilityId, facility?.commonFacilityType, getMinMaxReservationTime]);

  //예약시간 최소~최대 유효성 검사
  const selectedMinMaxTimeValidate = useCallback(() => {
    if (!userMinMaxTime || selectedSchedule.length === 0) return;

    const minTime = userMinMaxTime.minimumReservationTime;
    const maxTime = userMinMaxTime.maximumReservationTime;
    const isLimitedUnitReservationTime = userMinMaxTime.isLimitedUnitReservationTime; // 무제한 / 제한 적용여부

    const selectedSlotStart = selectedSchedule[0].startDate;
    const selectedSlotEnd = selectedSchedule[selectedSchedule.length - 1].endDate;

    const policyDisabled = reservationPolicy?.disabled;
    const policyStartTimes = reservationPolicy?.startTimes;

    // 선택한 slot의 총 시간(분)
    const totalTime = moment(selectedSlotEnd).diff(selectedSlotStart, "minutes");

    let minTimeText = "";
    let maxTimeText = "";
    let availableTimesText = "";
    let minTimeIsDisabled = false;
    let maxTimeIsDisabled = false;
    let availableTimesIsDisabled = false;

    // slot 1개당 30분

    // 제한이 있는 경우에만 검증
    if (isLimitedUnitReservationTime) {
      // 최소 예약시간 검증
      if (minTime > 0) {
        minTimeText = `1회 예약 시 최소 ${minutesToDisplayText(minTime)} 이상 예약할 수 있습니다.`;
        minTimeIsDisabled = minTime > totalTime;
      }

      // 최대 예약시간 검증
      if (maxTime > 0) {
        maxTimeText = `1회 예약 시 최대 ${minutesToDisplayText(maxTime)}까지 예약할 수 있습니다.`;
        maxTimeIsDisabled = maxTime < totalTime;
      }
    }

    // 예약시작 시간 가능을 적용할 경우 > ex) 13:00 / 13:30 / 15:00 에만 시작 가능
    // 예약 시작 시간 설정 적용 시
    if (policyDisabled === false) {
      // 정책의 요일은 ISO-8601 기준 (1=월 ~ 7=일)
      const policyStartDay = policyStartTimes?.find((item) => item.dayOfWeek === moment(selectedSlotStart).isoWeekday());
      if (policyStartDay) {
        const selectedStartMinutes = moment(selectedSlotStart).hour() * 60 + moment(selectedSlotStart).minute();
        const isValidStartTime = policyStartDay.offsetsInMinutes.includes(selectedStartMinutes);

        // 시작 가능한 시간이 여러개면 모두 병기
        const startTimes = policyStartDay.offsetsInMinutes.map((item) => minutesToHHMM(Number(item))).join(", ");

        availableTimesText = `${startTimes}부터 예약할 수 있습니다.`;
        availableTimesIsDisabled = !isValidStartTime;
      } else {
        availableTimesText = `예약할 수 있는 시간이 없습니다.`;
        availableTimesIsDisabled = true;
      }
    }

    // 한 번에 업데이트
    setReserveTimeValidation((prev) => ({
      ...prev,
      minTime: { message: minTimeText, isDisabled: minTimeIsDisabled },
      maxTime: { message: maxTimeText, isDisabled: maxTimeIsDisabled },
      availableTimes: { message: availableTimesText, isDisabled: availableTimesIsDisabled },
    }));
  }, [userMinMaxTime, reservationPolicy, selectedSchedule]);

  useEffect(() => {
    if (selectedSchedule.length > 0) {
      setReserveTimeValidation({
        minTime: { message: "", isDisabled: false },
        maxTime: { message: "", isDisabled: false },
        availableTimes: { message: "", isDisabled: false },
      });
      selectedMinMaxTimeValidate();
    } else {
      setReserveTimeValidation(validateDefault);
    }
  }, [selectedSchedule, selectedMinMaxTimeValidate]);

  useEffect(() => {
    if (facility) {
      fetchMinMaxReserveTime().then(() => {
        setIsDataLoaded(true);
      });
    }
  }, [facility, fetchMinMaxReserveTime]);

  // 공용공간 예약 영역 새로고침 > 공간상품(공용공간 포함) 상세 + 요금/점유/예약정책/예약가능구간 + 예약시간단위 재조회
  const onRefreshReservation = async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    setSelectedSchedule([]);
    try {
      await Promise.all([refetchProduct(), refetchSchedule(), fetchMinMaxReserveTime()]);
    } catch (error) {
      // 개별 api 의 에러는 useApiOperation 이 에러 모달로 처리한다. 여기서는 응답 파싱 실패 등의 rejection 이 새어나가지 않게만 막는다
      console.error("Error refreshing reservation:", error);
    } finally {
      isRefreshingRef.current = false;
    }
  };

  // 캘린더가 그린 날짜 범위 변경
  const onCalendarRangeChange = (startDate: string, endDate: string) => {
    setCalendarRange({
      startDate: moment(startDate).format(YmdFormat.WITH_TIME_ZONE),
      endDate: moment(endDate).format(YmdFormat.WITH_TIME_ZONE),
    });
  };

  // 스케줄 슬롯 선택시
  const onSelectSchedules = (schedule: ScheduleData) => {
    const sortedSchedule = [...selectedSchedule, schedule].sort((a, b) => a.slot - b.slot); // 선택한 시간 slot 기준 으로 sorting

    const startSlot = sortedSchedule[0].slot; // 가장 빠른 시간
    const endSlot = sortedSchedule[sortedSchedule.length - 1].slot; // 가장 마지막 시간

    const getSchedule = scheduleSlots.filter((item) => item.slot >= startSlot && item.slot <= endSlot);

    // 비활성화 구간 선택될 시 선택 안되도록 적용
    if (getSchedule.some((item) => item.isClosed)) {
      setSelectedSchedule([schedule]);
      return;
    }

    setSelectedSchedule(getSchedule);

    if (selectedSchedule.length > 0 && selectedSchedule.find((item) => item.slot === schedule.slot)) {
      setSelectedSchedule([]);
    }
  };

  const openIssueModal = async () => {
    // 점유 시도 전에 예약 가능 여부 확인
    const { data: dryRunData } = await postReservationDryRun({
      productId: String(product?.id),
      request: {
        start: moment(selectedSchedule[0].startDate).format(YmdFormat.WITH_TIME_ZONE),
        end: moment(selectedSchedule[selectedSchedule.length - 1].endDate).format(YmdFormat.WITH_TIME_ZONE),
        facilityId: String(facility?.id),
      },
    });
    const incompleteReasons = dryRunData.data.result.incompleteReasons;
    if (incompleteReasons.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: "예약이 어려워요",
        message: "아래의 사유로 인해 예약이 어려운 상태입니다. 확인하신 후 다시 예약해주세요.",
        type: "fail",
        payload: incompleteReasons,
        onClose: () => {
          setConfirmModal({ isOpen: false });
        },
      });
      return false;
    }
    return true;
  };

  // 이용 신청하기 버튼 -> 견적서 등록
  const onAddQuotaion = async () => {
    try {
      const isAvailableAddQuotaion = await openIssueModal();
      if (!isAvailableAddQuotaion) return;

      const token = await getToken("court/quotation/create");
      if (!token) return;

      const quotaionData: QuotationRequest = {
        token,
        siteKey,
        quotationItemList: [
          {
            productId: String(product?.id),
            buildingCommonFacilityId: String(facility?.id),
            startDate: selectedSchedule[0].startDate, //

            endDate: selectedSchedule[selectedSchedule.length - 1].endDate,
          },
        ],
      };
      const { data, status } = await postQuotation(quotaionData);

      if (status >= 200 && status <= 299) {
        const result = data.data.content;
        navigate(`/court/quotation/${result.quotationNo}`);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <>
      <MetaTag title="공간상세 | TaapSpace" isReCAPTCHA={selectedSchedule.length < 1 ? false : true} reCaptchaMode="policy-based" />
      <Header headerType="BACK" title={facility?.facilityName} rightArea={<Link to="/court/main" className="home"></Link>} />

      <div className="facility-detail">
        <SwiperViewer medias={mediaSorting(facility?.mediaList || [])} alt={`공용공간이미지-${facility?.facilityName}`} />
        <article className="facility-info">
          {/* 메인정보 > [헤더 이미지, 정원, 비용등]*/}
          <FacilityUsageInfo minimumPrice={minimumPrice} facility={facility} />

          {/* 예약부분 > [캘린더. 시간선택] */}
          {product?.productType === "TIME_COURT" && (
            <FacilityReservation
              date={calendardate}
              selectedDate={setCalendarDate}
              scheduleSlots={scheduleSlots}
              onSelectSchedules={onSelectSchedules}
              selectedSchedule={selectedSchedule}
              onCalendarRangeChange={onCalendarRangeChange}
              closedDays={closedDays}
              onResetSchedule={() => setSelectedSchedule([])}
              reserveTimeValidation={reserveTimeValidation}
              updatedAt={updatedAt}
              onRefresh={onRefreshReservation}
              reserveDuration={reserveDuration}
            />
          )}

          {/* 공간정보 */}
          {facility?.spaceStyle !== "SPACE_STYLE_UNRECOGNIZED" && (
            <section className="facility-info__facility-info top-divider">
              <h2>공간 정보</h2>
              <div className="info-line">
                <span className="minmax90">시설안내</span>
                <p className="pre-formatted">{facility?.facilityList?.join(",")}</p>
              </div>
              <div className="info-line">
                <span className="minmax90">이용안내</span>
                <p className="pre-formatted">{facility?.description}</p>
              </div>
            </section>
          )}

          {/* 건물 정보 */}
          <TaapSpaceBuilding building={{ ...building }} isOverview />
        </article>

        {product?.productType === "TIME_COURT" ? (
          <div className="base-floating-btn-wrap border-top ">
            {/* <div>{drawTotalPrice && drawTotalPrice()}</div> */}
            {/* 상품 노출 시작시간 / 종료시간 조건  확인 후 메시지 노출*/}
            {availableSalePeriod?.isAvailableSaleStartDate && availableSalePeriod?.isAvailableSaleEndDate ? (
              selectedSchedule.length > 0 && <ReservationTimeAndPrice selectedSchedule={selectedSchedule} />
            ) : (
              <div className="flex-center ">
                <div className="w-100 text-center my10">
                  <p className="mb10 font16 font-weight-600">{availableSalePeriod?.title}</p>
                  <p className="mb10 font14">
                    {availableSalePeriod?.message &&
                    availableSalePeriod.message.includes("오픈일시") &&
                    availableSalePeriod.message.includes("마감일시")
                      ? availableSalePeriod.message.replace("\n마감일시", " / 마감일시")
                      : availableSalePeriod?.message}
                  </p>
                </div>
              </div>
            )}

            {product && facility && isDataLoaded && (
              <div className="flex-center gap-10">
                <button
                  className={product.salePeriodType !== "INQUIRY" ? "base-btn color-white" : "base-btn"}
                  style={{ width: product.salePeriodType !== "INQUIRY" ? "100px" : "100%" }}
                  onClick={() => {
                    const prevURL = getBaseUrl() + location.pathname;
                    const state = {
                      prevURL,
                    };
                    navigate(`/court/product/${productId}/inquiry?facilityId=${facilityId}`, { state });
                  }}
                >
                  문의하기
                </button>
                {product && product.salePeriodType !== "INQUIRY" && (
                  <BaseButton
                    title="예약하기"
                    onClick={onAddQuotaion}
                    disabled={
                      Object.values(reserveTimeValidation).some((item) => item.isDisabled) ||
                      !(availableSalePeriod?.isAvailableSaleStartDate && availableSalePeriod?.isAvailableSaleEndDate) // 상품 판매가능 기간 조건
                    }
                    delay={700}
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="base-floating-btn-wrap">
            {product && facility && isDataLoaded && (
              <button
                className="base-btn color-white"
                style={{ width: "100px" }}
                onClick={() => {
                  const prevURL = getBaseUrl() + location.pathname;
                  const state = {
                    prevURL,
                  };
                  navigate(`/court/product/${productId}/inquiry?facilityId=${facilityId}`, { state });
                }}
              >
                문의하기
              </button>
            )}
          </div>
        )}
      </div>
      {confirmModal.isOpen && (
        <IssueModalRenderer
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type as "delay" | "existing" | "fail"}
          onClick={() => {
            confirmModal.onClick && confirmModal.onClick();
          }}
          onClose={() => (confirmModal.onClose ? confirmModal.onClose() : setConfirmModal({ isOpen: false }))}
          payload={confirmModal.payload}
        />
      )}
    </>
  );
};
export default FacilityDetail;
